package br.gov.go.pge.permanenciaweb.site.components

import androidx.compose.runtime.*
import br.gov.go.pge.permanenciaweb.shared.DirectoryPersonDto
import br.gov.go.pge.permanenciaweb.site.IndustrialPalette
import com.varabyte.kobweb.compose.css.*
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.foundation.layout.Spacer
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.forms.Button
import com.varabyte.kobweb.silk.components.forms.ButtonSize
import com.varabyte.kobweb.silk.components.forms.ButtonStyle
import com.varabyte.kobweb.silk.components.forms.ButtonVars
import com.varabyte.kobweb.silk.components.icons.PlusIcon
import com.varabyte.kobweb.silk.components.icons.TrashIcon
import com.varabyte.kobweb.silk.components.overlay.Overlay
import com.varabyte.kobweb.silk.style.*
import kotlinx.browser.window
import kotlinx.coroutines.delay
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.attributes.placeholder
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.css.JustifyContent
import org.jetbrains.compose.web.dom.*
import org.w3c.dom.HTMLInputElement
import org.w3c.dom.events.EventListener
import org.w3c.dom.events.KeyboardEvent

/*
 * Person picker for the "Liberações" form.
 *
 * Server-side search (the directory has ~5k rows), so the dialog only renders what the operator is
 * looking at. Two entry modes share the same table: "add" (a new empty slot) and "swap" (the person
 * being replaced is pinned first, highlighted and without a select button).
 *
 * While a search is in flight the previous rows stay on screen (no blink) and a small spinner
 * overlays the top of the results.
 */

val PickerCardStyle = CssStyle.base {
    Modifier
        .backgroundColor(IndustrialPalette.White)
        .border(1.px, LineStyle.Solid, IndustrialPalette.Border)
        .borderRadius(0.375.cssRem)
        .padding(1.25.cssRem)
        // Wide enough for the seven columns without squeezing them; the table scrolls if needed.
        .maxWidth(64.cssRem)
        .width(94.percent)
}

val PickerSearchStyle = CssStyle.base {
    Modifier
        .width(100.percent)
        .color(IndustrialPalette.Ink)
        .fontSize(0.8125.cssRem)
        .backgroundColor(IndustrialPalette.White)
        .border(1.px, LineStyle.Solid, IndustrialPalette.Border)
        .borderRadius(0.25.cssRem)
        .padding(0.375.cssRem, 0.625.cssRem)
}

/**
 * Scroll container for the results. It keeps a fixed height so opening the dialog (empty) and
 * filling it (30 rows) do not resize the card, and it owns the spinner's positioning context.
 */
val PickerResultsStyle = CssStyle.base {
    Modifier
        .width(100.percent)
        .minHeight(6.cssRem)
        .maxHeight(22.cssRem)
        .overflow(Overflow.Auto)
        .margin(top = 0.5.cssRem)
}

val PickerResultsInnerStyle = CssStyle.base {
    Modifier.styleModifierRelative()
}

private fun Modifier.styleModifierRelative(): Modifier =
    this.styleModifier { property("position", "relative") }

/** Small pill at the top centre of the results, over the stale rows. */
val PickerSpinnerStyle = CssStyle.base {
    Modifier
        .backgroundColor(IndustrialPalette.White)
        .border(1.px, LineStyle.Solid, IndustrialPalette.Border)
        .borderRadius(0.75.cssRem)
        .padding(0.125.cssRem, 0.5.cssRem)
        .fontSize(0.875.cssRem)
        .color(IndustrialPalette.Steel)
        .styleModifierSpinnerPosition()
}

private fun Modifier.styleModifierSpinnerPosition(): Modifier =
    this.styleModifier {
        property("position", "sticky")
        property("top", "0.25rem")
        property("left", "50%")
        property("transform", "translateX(-50%)")
        property("z-index", "2")
    }

sealed interface PickerRowKind : com.varabyte.kobweb.silk.style.ComponentKind

val PickerRowStyle = CssStyle<PickerRowKind> {
    base { Modifier }
}

/** The person being swapped: visually distinct and pinned to the first row. */
val PickerPinnedRowVariant = PickerRowStyle.addVariantBase {
    Modifier.backgroundColor(IndustrialPalette.SteelTint)
}

/** Rows that can be picked: the whole row is clickable. */
val PickerClickableRowVariant = PickerRowStyle.addVariantBase {
    Modifier.cursor(Cursor.Pointer)
}

/** Full-width, left-aligned, chrome-free button: the row itself, minus the trash. */
val PersonRowButtonVariant = ButtonStyle.addVariant {
    base {
        Modifier
            .setVariable(ButtonVars.BackgroundDefaultColor, Colors.Transparent)
            .setVariable(ButtonVars.BackgroundHoverColor, IndustrialPalette.RowHover)
            .setVariable(ButtonVars.BackgroundPressedColor, IndustrialPalette.RowHover)
            .setVariable(ButtonVars.Color, IndustrialPalette.Ink)
            .fontWeight(FontWeight.Normal)
            .border(0.px, LineStyle.Solid, Colors.Transparent)
            .borderRadius(0.1875.cssRem)
            .padding(0.25.cssRem, 0.375.cssRem)
            .width(100.percent)
            .styleModifier { property("justify-content", "flex-start") }
    }
}

/** Text for assistive tech only: the icon-only trash needs an accessible name. */
val ScreenReaderOnlyStyle = CssStyle.base {
    Modifier.styleModifier {
        property("position", "absolute")
        property("width", "1px")
        property("height", "1px")
        property("padding", "0")
        property("margin", "-1px")
        property("overflow", "hidden")
        property("clip", "rect(0 0 0 0)")
        property("white-space", "nowrap")
        property("border", "0")
    }
}

val PickerMutedStyle = CssStyle.base {
    Modifier.color(IndustrialPalette.Muted).fontSize(0.75.cssRem)
}

/** Table cells that must not wrap: wrapping is what made the columns look squeezed. */
val PickerCellStyle = TableCellStyle.extendedByBase {
    Modifier.whiteSpace(WhiteSpace.NoWrap)
}

@Composable
internal fun PersonPickerDialog(
    open: Boolean,
    query: String,
    onQueryChange: (String) -> Unit,
    results: List<DirectoryPersonDto>,
    loading: Boolean,
    selectedIds: Set<String>,
    pinned: DirectoryPersonDto?,
    swapMode: Boolean,
    error: String,
    onPick: (DirectoryPersonDto) -> Unit,
    onClose: () -> Unit,
) {
    if (!open) return

    DisposableEffect(Unit) {
        val listener = EventListener { event ->
            if ((event as? KeyboardEvent)?.key == "Escape") onClose()
        }
        window.addEventListener("keydown", listener)
        onDispose { window.removeEventListener("keydown", listener) }
    }

    Overlay(contentAlignment = Alignment.Center) {
        Box(Modifier.fillMaxSize().onClick { onClose() })

        Column(modifier = PickerCardStyle.toModifier().then(Modifier.id("person-picker"))) {
            H3(attrs = Modifier.margin(0.cssRem).fontSize(1.cssRem).toAttrs()) {
                Text(if (swapMode) "Trocar pessoa" else "Adicionar pessoa")
            }
            P(attrs = PickerMutedStyle.toModifier().margin(top = 0.25.cssRem, bottom = 0.25.cssRem).toAttrs()) {
                Text("Busque pelo nome. A lista vem do cadastro de pessoas que podem entrar no prédio.")
            }

            Input(
                type = InputType.Text,
                attrs = PickerSearchStyle.toAttrs {
                    attr("id", "person-picker-search")
                    attr("aria-label", "Buscar pessoa por nome")
                    placeholder("Buscar por nome")
                    onInput { event ->
                        onQueryChange((event.target as? HTMLInputElement)?.value.orEmpty())
                    }
                },
            )

            if (error.isNotEmpty()) {
                P(attrs = Modifier.color(IndustrialPalette.Red).fontSize(0.75.cssRem).margin(top = 0.375.cssRem).toAttrs()) {
                    Text(error)
                }
            }

            Div(attrs = PickerResultsStyle.toAttrs()) {
                Div(attrs = PickerResultsInnerStyle.toAttrs()) {
                    if (loading) SearchSpinner()
                    when {
                        results.isNotEmpty() -> PickerTable(
                            results = results,
                            selectedIds = selectedIds,
                            pinned = pinned,
                            swapMode = swapMode,
                            onPick = onPick,
                        )

                        loading -> Unit

                        query.isBlank() -> P(attrs = PickerMutedStyle.toModifier().toAttrs()) {
                            Text("Digite um nome para buscar.")
                        }

                        else -> P(attrs = PickerMutedStyle.toModifier().toAttrs()) {
                            Text("Nenhuma pessoa encontrada.")
                        }
                    }
                }
            }

            Row(
                modifier = Modifier
                    .width(100.percent)
                    .gap(0.5.cssRem)
                    .margin(top = 0.75.cssRem),
            ) {
                Spacer()
                Button(onClick = { onClose() }, variant = DefaultButtonVariant, size = ButtonSize.SM) {
                    Text("Cancelar")
                }
            }
        }
    }
}

private val SPINNER_FRAMES = listOf("⠋", "⠙", "⠹", "⠸", "⠼", "⠴", "⠦", "⠧", "⠇", "⠏")

/** Braille spinner: no CSS keyframes needed, and it never replaces the stale rows. */
@Composable
private fun SearchSpinner() {
    var frame by remember { mutableStateOf(0) }
    LaunchedEffect(Unit) {
        while (true) {
            delay(100)
            frame = (frame + 1) % SPINNER_FRAMES.size
        }
    }
    Div(
        attrs = PickerSpinnerStyle.toModifier().toAttrs {
            attr("role", "status")
            attr("aria-label", "Buscando")
        },
    ) {
        Text(SPINNER_FRAMES[frame])
    }
}

@Composable
private fun PickerTable(
    results: List<DirectoryPersonDto>,
    selectedIds: Set<String>,
    pinned: DirectoryPersonDto?,
    swapMode: Boolean,
    onPick: (DirectoryPersonDto) -> Unit,
) {
    val rows = buildList {
        if (pinned != null) add(pinned)
        addAll(results.filter { it.idpessoa != pinned?.idpessoa })
    }

    Table(attrs = TableStyle.toAttrs()) {
        Thead {
            Tr {
                Th(attrs = TableHeaderCellStyle.toAttrs()) { Text("Nome") }
                Th(attrs = TableHeaderCellStyle.toAttrs()) { Text("Andar") }
                Th(attrs = TableHeaderCellStyle.toAttrs()) { Text("Sala") }
                Th(attrs = TableHeaderCellStyle.toAttrs()) { Text("Unidade") }
                Th(attrs = TableHeaderCellStyle.toAttrs()) { Text("CPF") }
                Th(attrs = TableHeaderCellStyle.toAttrs()) { Text("Cargo") }
                Th(attrs = TableHeaderCellStyle.toAttrs()) { }
            }
        }
        Tbody {
            for (person in rows) {
                val isPinned = person.idpessoa == pinned?.idpessoa
                val already = person.idpessoa in selectedIds && !isPinned
                val clickable = !isPinned && !already
                Tr(
                    attrs = PickerRowStyle
                        .toModifier(
                            when {
                                isPinned -> PickerPinnedRowVariant
                                clickable -> PickerClickableRowVariant
                                else -> null
                            }
                        )
                        .toAttrs {
                            if (clickable) onClick { onPick(person) }
                        },
                ) {
                    Td(attrs = PickerCellStyle.toAttrs()) {
                        Text(person.nome)
                        if (isPinned) {
                            Div(attrs = PickerMutedStyle.toModifier().toAttrs()) { Text("pessoa atual") }
                        }
                    }
                    Td(attrs = PickerCellStyle.toAttrs()) { Text(person.andar.orEmpty()) }
                    Td(attrs = PickerCellStyle.toAttrs()) { Text(person.sala.orEmpty()) }
                    Td(attrs = PickerCellStyle.toAttrs()) { Text(person.unidade.orEmpty()) }
                    Td(attrs = PickerCellStyle.toAttrs()) { Text(person.cpfMascarado.orEmpty()) }
                    Td(attrs = PickerCellStyle.toAttrs()) { Text(person.cargo.orEmpty()) }
                    Td(attrs = PickerCellStyle.toAttrs()) {
                        when {
                            isPinned -> Unit // the current person has nothing to select
                            already -> P(attrs = PickerMutedStyle.toModifier().margin(0.cssRem).toAttrs()) {
                                Text("já adicionada")
                            }

                            else -> Button(
                                onClick = { event ->
                                    event.stopPropagation()
                                    onPick(person)
                                },
                                // Same blue as "Trocar": the action reads the same in both modes.
                                variant = PrimaryButtonVariant,
                                size = ButtonSize.SM,
                            ) {
                                Text(if (swapMode) "Trocar" else "Selecionar")
                            }
                        }
                    }
                }
            }
        }
    }
}

/**
 * The field that opens the picker: a non-writable control (a button, not an input — it can only be
 * changed through the dialog, so it must not pretend to be typeable) plus one removable chip per
 * selected person. The whole row reopens the picker in "swap" mode; only the trash button removes.
 */
@Composable
internal fun PeopleField(
    people: List<DirectoryPersonDto>,
    onAdd: () -> Unit,
    onSwap: (DirectoryPersonDto) -> Unit,
    onRemove: (DirectoryPersonDto) -> Unit,
) {
    Column(modifier = Modifier.width(100.percent).gap(0.375.cssRem)) {
        if (people.isEmpty()) {
            Button(
                onClick = { onAdd() },
                variant = DefaultButtonVariant,
                size = ButtonSize.SM,
                modifier = Modifier.width(100.percent).then(Modifier.id("people-empty")),
            ) {
                Div(attrs = Modifier.width(100.percent).textAlign(TextAlign.Start).color(IndustrialPalette.Muted).toAttrs()) {
                    Text("Nenhuma pessoa selecionada")
                }
            }
        } else {
            for (person in people) {
                // Two sibling buttons, never a button inside a clickable div: the wide one is the
                // row (swap) and the trash is its own control, so screen readers see two actions.
                Div(
                    attrs = Modifier
                        .width(100.percent)
                        .styleModifier {
                            property("display", "grid")
                            property("grid-template-columns", "1fr auto")
                            property("align-items", "center")
                        }
                        .gap(0.375.cssRem)
                        .padding(0.125.cssRem)
                        .backgroundColor(IndustrialPalette.Surface)
                        .borderRadius(0.25.cssRem)
                        .toAttrs(),
                ) {
                    Button(
                        onClick = { onSwap(person) },
                        variant = PersonRowButtonVariant,
                        size = ButtonSize.SM,
                    ) {
                        // Name and details share one baseline row: the details used to sit at the
                        // top because they were separate flex children of the button.
                        Row(
                            // Silk centres button content; the row must read as a list line.
                            modifier = Modifier.width(100.percent).justifyContent(JustifyContent.Start).gap(0.5.cssRem),
                            verticalAlignment = Alignment.CenterVertically,
                        ) {
                            Text(person.nome)
                            Span(attrs = PickerMutedStyle.toModifier().toAttrs()) {
                                Text(
                                    listOfNotNull(person.sala?.let { "sala $it" }, person.andar)
                                        .joinToString(" · ")
                                )
                            }
                        }
                    }
                    Button(
                        onClick = { onRemove(person) },
                        variant = DangerButtonVariant,
                        size = ButtonSize.SM,
                    ) {
                        TrashIcon(Modifier.fontSize(0.8125.cssRem))
                        Span(attrs = ScreenReaderOnlyStyle.toAttrs()) { Text("Remover ${person.nome}") }
                    }
                }
            }
        }

        // `verticalAlignment` is the Row's own align-items: the hint sits centred against the button.
        Row(
            modifier = Modifier.gap(0.5.cssRem),
            verticalAlignment = Alignment.CenterVertically,
        ) {
            Button(
                onClick = { onAdd() },
                variant = DefaultButtonVariant,
                size = ButtonSize.SM,
                modifier = Modifier.id("people-add"),
            ) {
                PlusIcon(Modifier.fontSize(0.8125.cssRem).margin(right = 0.25.cssRem))
                Text("Adicionar pessoa")
            }
            if (people.isNotEmpty()) {
                P(attrs = PickerMutedStyle.toModifier().margin(0.cssRem).toAttrs()) {
                    Text("Clique no nome para trocar a pessoa.")
                }
            }
        }
    }
}
